"use client";

import PlatformCard from "@/app/[locale]/(platform)/sharedComponents/platformCard";
import { GetFoldersResponseType } from "../../../../hooks/useGetFolders";
import { useStorageQuery } from "../../../../hooks/storageQueryProvider";
import FolderOptions from "./optionsMenu/folderOptions";
import { Folder } from "lucide-react";
import { useMemo } from "react";
import { View } from "../../types";
import PlatformListItem from "@/app/[locale]/(platform)/sharedComponents/platformListItem";
import { t } from "@/lib/localization";

export default function FolderCard({
    folder,
    view,
}: {
    folder: GetFoldersResponseType["data"][0];
    view: View;
}) {
    const {
        selectedFolders,
        setSelectedFolders,
        settings,
        changeParentFolder,
    } = useStorageQuery();

    const selected = useMemo(
        () => selectedFolders.some((f) => f.id === folder.id),
        [selectedFolders, folder]
    );

    const onFolderClick = () => {
        if (
            settings.folders.onFolderClickActions.includes("navigate-to-folder")
        ) {
            changeParentFolder(folder);
        }

        if (settings.folders.onFolderClickActions.includes("select-folder")) {
            setSelectedFolders((prev) => {
                const isSelected = prev.some((f) => f.id === folder.id);
                const isNavigating =
                    settings.folders?.onFolderClickActions.includes(
                        "navigate-to-folder"
                    );

                if (isSelected) {
                    if (isNavigating) {
                        // do nothing, keep it selected
                        return prev;
                    } else {
                        // unselect file
                        return prev.filter((f) => f.id !== folder.id);
                    }
                } else {
                    return [...prev, folder];
                }
            });
        }
    };

    if (view === "grid") {
        return (
            <PlatformCard
                onClick={onFolderClick}
                selected={selected}
                options={
                    settings.folders.menuOptions
                        ? {
                              component: (
                                  <FolderOptions
                                      folder={folder}
                                      selected={selected}
                                  />
                              ),
                              positionClassName: "end-1 top-5",
                          }
                        : undefined
                }
                text={{
                    icon: Folder,
                    title: folder.name,
                    subtitle: getFolderCardSubtitle(folder),
                }}
            />
        );
    }

    return (
        <PlatformListItem
            onClick={onFolderClick}
            selected={selected}
            options={
                settings.folders.menuOptions ? (
                    <FolderOptions folder={folder} selected={selected} />
                ) : undefined
            }
            text={{
                icon: Folder,
                title: folder.name,
                subtitle: getFolderCardSubtitle(folder),
            }}
        />
    );
}

const getFolderCardSubtitle = (folder: GetFoldersResponseType["data"][0]) => {
    const { fileCount, subfolderCount } = folder;

    if (!fileCount && !subfolderCount) {
        return t("storage.folder-card.empty");
    }
    if (!subfolderCount) {
        return t("storage.folder-card.files-count", undefined, {
            count: fileCount || 0,
        });
    }
    if (!fileCount) {
        return t("storage.folder-card.folders-count", undefined, {
            count: subfolderCount,
        });
    }
    return t("storage.folder-card.folders-files-count", undefined, {
        folders: subfolderCount,
        files: fileCount,
    });
};
